import React, { useState } from "react";
import {
  Home,
  Calendar,
  BriefcaseBusiness,
  MessageSquare,
  Users,
  Star,
  Settings,
  LogOut,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SearchBar } from "../components/ui/search-bar";
import { useAuth } from "../contexts/AuthContext";

export function Sidebar({ navItems = defaultNavItems, onSearch }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const { user, logout } = useAuth();

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (onSearch) onSearch(query);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="sidebar">
      <div className="top-section">
        {/* Logo */}
        <div className="logo">
          <Link to="/dashboard" className="logo-link">
            <img src="/carepetai-logo.svg" alt="Logo" className="logo" />
          </Link>
        </div>

        {/* Search */}
        <div className="search-container">
          <SearchBar
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Search"
          />
        </div>

        {/* Navigation */}
        <nav className="nav-container">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`nav-link ${isActive ? "active" : ""}`}
              >
                {item.icon}
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
      </div>
      <div className="bottom-section">
        {/* Settings */}
        <div className="settings-container">
          <Link
            to="/settings"
            className={`nav-link ${location.pathname === "/settings" ? "active" : ""}`}
          >
            <Settings className="icon" />
            <span>Settings</span>
          </Link>

          {/* Divider */}
          <div>
            <div className="divider" />

            {/* User Profile */}
            <div className="user-profile">
              <div className="user-info">
                <div className="avatar">
                  {user.avatar ? (
                    <img
                      src={user.avatar || "/placeholder.svg"}
                      alt={user.first_name}
                      className="avatar-image"
                    />
                  ) : (
                    <div className="avatar-fallback">
                      {user.first_name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </div>
                  )}
                </div>
                <div className="user-details">
                  <div className="user-name">
                    {user.first_name} {user.last_name}
                  </div>
                  <span className="user-email">{user.email}</span>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="logout-button"
                aria-label="Logout"
              >
                <LogOut className="icon" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add CSS styles */}
      <style jsx>{`
        @import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap");

        .sidebar {
          position: sticky;
          display: flex;
          flex-direction: column;
          height: 90vh;
          width: 16rem;
          flex-shrink: 0;
          background-color: var(--background);
          padding: 1.5rem 1rem;
          left: 0;
          top: 0;
          z-index: 10;
          border-right: 1px solid var(--light-gray);
          font-family:
            "Inter",
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            Roboto,
            Oxygen,
            Ubuntu,
            sans-serif;
        }

        .logo {
          width: 150px;
          margin-bottom: 1rem;
          padding: 0 0.5rem;
        }

        .search-container {
          margin-bottom: 1.5rem;
          padding: 0 0.5rem;
        }

        .nav-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .nav-link {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          border-radius: 0.5rem;
          padding: 0.625rem 0.75rem;
          font-size: 0.875rem;
          font-weight: 500;
          background: none;
          text-decoration: none;
          transition: all 0.2s;
          color: var(--icon, #687076);
          font-family:
            "Inter",
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            Roboto,
            Oxygen,
            Ubuntu,
            sans-serif;
        }

        .nav-link:hover {
          background-color: var(--light-gray);
          color: var(--text, #000000);
          transform: translateX(2px);
        }

        .nav-link.active {
          background-color: var(--light-gray);
          color: var(--primary, #ff5a00);
          font-weight: 600;
          border-left: 3px solid var(--primary, #ff5a00);
        }

        .icon {
          height: 1.25rem;
          width: 1.25rem;
          flex-shrink: 0;
          color: currentColor;
        }

        .bottom-section {
          margin-top: auto;
          flex-shrink: 0;
          padding-top: 1rem;
        }

        .settings-container {
          margin-top: auto;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .divider {
          border-top: 1px solid var(--light-gray);
          margin: 0.5rem 0;
        }

        .user-profile {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
          min-width: 0;
        }

        .avatar {
          height: 2.5rem;
          width: 2.5rem;
          border-radius: 9999px;
          overflow: hidden;
          background: var(--primary, #ff5a00);
          flex-shrink: 0;
          border: 2px solid transparent;
          transition: border-color 0.2s;
        }

        .avatar:hover {
          border-color: var(--primary, #ff5a00);
        }

        .avatar-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .avatar-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.875rem;
          font-weight: 600;
          color: white;
          background: var(--primary, #ff5a00);
          font-family:
            "Inter",
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            Roboto,
            Oxygen,
            Ubuntu,
            sans-serif;
        }

        .user-details {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        .user-name {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--text, #000000);
          font-family:
            "Inter",
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            Roboto,
            Oxygen,
            Ubuntu,
            sans-serif;
        }

        .user-email {
          font-size: 0.75rem;
          color: var(--icon, #687076);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-family:
            "Inter",
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            Roboto,
            Oxygen,
            Ubuntu,
            sans-serif;
          font-weight: 400;
        }

        .logout-button {
          border-radius: 0.375rem;
          padding: 0.5rem;
          color: var(--icon, #687076);
          background: none;
          border: none;
          cursor: pointer;
          transition: all 0.2s;
          flex-shrink: 0;
          margin-left: 0.5rem;
          font-family:
            "Inter",
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            Roboto,
            Oxygen,
            Ubuntu,
            sans-serif;
        }

        .logout-button:hover {
          background-color: var(--light-gray);
          color: var(--error, #ff003c);
          transform: scale(1.05);
        }

        /* Improved font rendering */
        * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        /* Dark mode support */
        @media (prefers-color-scheme: dark) {
          .nav-link {
            color: var(--icon, #9ba1a6);
          }

          .nav-link:hover {
            color: var(--gray);
          }

          .nav-link.active {
            color: var(--primary, #ff5a00);
          }

          .user-name {
            color: var(--text, #ffffff);
          }

          .user-email {
            color: var(--icon, #9ba1a6);
          }

          .logout-button {
            color: var(--icon, #9ba1a6);
          }

          .logout-button:hover {
            color: var(--error, #ff003c);
          }
        }
      `}</style>
    </div>
  );
}

// Updated default navigation items to match your routes
export const defaultNavItems = [
  { label: "Home", href: "/dashboard", icon: <Home className="icon" /> },
  {
    label: "Appointments",
    href: "/appointments",
    icon: <Calendar className="icon" />,
  },
  {
    label: "Services",
    href: "/services",
    icon: <BriefcaseBusiness className="icon" />,
  },
  {
    label: "Messages",
    href: "/chats",
    icon: <MessageSquare className="icon" />,
  },
  {
    label: "Clients",
    href: "/clients",
    icon: <Users className="icon" />,
  },
  {
    label: "Reviews",
    href: "/reviews",
    icon: <Star className="icon" />,
  },
];

export default Sidebar;
